import { getUserInfo } from './auth';
import { getApiV1Url } from '../config/api';

export interface AppListItem {
  projectCode: string;
  code: string;
  status: string;
  type: string;
  name: string;
  note?: string;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface AppListResponse {
  timestamp: number;
  errors?: {
    error?: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: {
    totalCount: number;
    apps: AppListItem[];
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface GetAppListParams {
  status?: string;
  type?: string;
}

export interface AppCreateRequest {
  code: string;
  status: string;
  type: string;
  name: string;
  note?: string;
}

/** 앱 상세 응답 data */
export interface AppDetail {
  projectCode: string;
  code: string;
  status: string;
  type: string;
  name: string;
  note?: string;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface AppDetailResponse {
  timestamp: number;
  errors?: {
    error?: Array<{ code: number; target: string; message: string }>;
  };
  data: AppDetail;
}

/**
 * 앱 목록 조회
 * GET /api/v1/projects/{projectCode}/apps?status=&type=&size=0
 */
export const getAppList = async (
  projectCode: string,
  params?: GetAppListParams
): Promise<{
  apps: AppListItem[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const queryParams = new URLSearchParams({
    size: '0',
    page: '0',
  });
  if (params?.status) queryParams.append('status', params.status);
  if (params?.type) queryParams.append('type', params.type);

  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/apps?${queryParams}`),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Account-Code': userInfo.accountCode,
        'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result: AppListResponse = await response.json();

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  return {
    apps: result.data.apps ?? [],
    totalCount: result.data.totalCount ?? 0,
    totalPages: result.data.totalPages ?? 0,
    currentPage: result.data.currentPage ?? 0,
    pageSize: result.data.pageSize ?? 0,
    hasNext: result.data.hasNext ?? false,
    hasPrevious: result.data.hasPrevious ?? false,
  };
};

/**
 * 앱 생성
 * POST /api/v1/projects/{projectCode}/apps
 */
export const createApp = async (
  projectCode: string,
  body: AppCreateRequest
): Promise<{ success: boolean; message?: string; data?: AppListItem }> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const response = await fetch(getApiV1Url(`/projects/${projectCode}/apps`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
    },
    body: JSON.stringify({
      code: body.code.trim(),
      status: body.status,
      type: body.type,
      name: body.name.trim(),
      ...(body.note != null && body.note !== '' ? { note: body.note.trim() } : {}),
    }),
  });

  const result = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e: { message?: string }) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return { success: true, data: result.data };
};

/**
 * 앱 상세 조회
 * GET /api/v1/projects/{projectCode}/apps/{appCode}
 */
export const getAppDetail = async (
  projectCode: string,
  appCode: string
): Promise<AppDetail | null> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/apps/${encodeURIComponent(appCode)}`),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Account-Code': userInfo.accountCode,
        'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
      },
    }
  );

  const result: AppDetailResponse = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error((result as any).message || `HTTP error! status: ${response.status}`);
  }

  return result.data ?? null;
};

/**
 * 앱 수정
 * POST /api/v1/projects/{projectCode}/apps/{appCode}
 */
export const updateApp = async (
  projectCode: string,
  appCode: string,
  body: Omit<AppCreateRequest, 'code'>
): Promise<{ success: boolean; message?: string; data?: AppDetail }> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/apps/${encodeURIComponent(appCode)}`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Account-Code': userInfo.accountCode,
        'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
      },
      body: JSON.stringify({
        code: appCode,
        status: body.status,
        type: body.type,
        name: body.name.trim(),
        ...(body.note != null && body.note !== '' ? { note: body.note.trim() } : {}),
      }),
    }
  );

  const result = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e: { message?: string }) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return { success: true, data: result.data };
};